import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useQuery } from '@apollo/client/react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { db } from '@/lib/local-api';
import { useCurrentChat } from '~/chat/hooks/useCurrentChat';
import { Chat } from '~/chat/components/chat/Chat';

/**
 * Landing screen for the bare `/chat` route. The single place the conversation
 * lifecycle runs (mounted once per navigation, not per subtree):
 *
 * 1. Ensures a chat exists once a user is known.
 * 2. Redirects to `/chat/:niceId` as soon as the resolved chat has a `niceId`.
 *    While it's still null (offline / just-created), it holds — rendering the
 *    chat window (resolved by uuid via `useCurrentChat`) until the niceId syncs
 *    in. The surrounding chrome (header, conversation list) lives in `ChatLayout`.
 */
export const ChatIndexScreen = () => {
  const powerSync = usePowerSync();
  const router = useRouter();
  const { t } = useTranslation('chat');
  const { data } = useQuery(CurrentUserDocument);
  const { chat, isLoading } = useCurrentChat();
  const userId = data?.currentUser?.id ?? '';
  const defaultName = t('list.defaultName');

  // Ensure a conversation exists. `getOrCreateChat` re-checks for an existing
  // chat before inserting, so this stays idempotent across re-renders.
  useEffect(() => {
    if (!userId || isLoading || chat) return;
    let cancelled = false;
    db.Chat.mutations
      .getOrCreateChat(powerSync, userId, defaultName)
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to initialize chat session:', error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [powerSync, userId, defaultName, isLoading, chat]);

  // Canonicalize the URL to the shareable niceId once the backend backfills it.
  useEffect(() => {
    if (chat?.niceId) {
      router.replace(`/chat/${chat.niceId}`);
    }
  }, [chat?.niceId, router]);

  return <Chat />;
};
